import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUserDetailPage } from './useAdminUserDetailPage';
import AdminUserDetailAssistantSection from './AdminUserDetailAssistantSection';
import AdminUserDetailCreditsSection from './AdminUserDetailCreditsSection';
import AdminUserDetailDefaultPromptSection from './AdminUserDetailDefaultPromptSection';
import AdminUserDetailInboundSection from './AdminUserDetailInboundSection';
import AdminUserDetailDeleteConfigModal from './AdminUserDetailDeleteConfigModal';
import AdminUserDetailDeleteUserModal from './AdminUserDetailDeleteUserModal';

const AdminUserDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    successMessage,
    setSuccessMessage,
    editing,
    editForm,
    setEditForm,
    userInfo,
    assistantConfig,
    inboundConfig,
    loadingInboundConfig,
    inboundEditForm,
    setInboundEditForm,
    savingInboundConfig,
    addCreditsAmount,
    setAddCreditsAmount,
    addCreditsDescription,
    setAddCreditsDescription,
    addCreditsLoading,
    defaultPromptContent,
    setDefaultPromptContent,
    loadingDefaultPrompt,
    savingDefaultPrompt,
    editingDefaultPrompt,
    setEditingDefaultPrompt,
    editingInboundConfig,
    setEditingInboundConfig,
    showDeleteModal,
    setShowDeleteModal,
    deletingConfig,
    showDeleteUserModal,
    setShowDeleteUserModal,
    deleteUserConfirmValue,
    setDeleteUserConfirmValue,
    deletingUser,
    deletionStats,
    setDeletionStats,
    poolNumber,
    loadingPoolNumber,
    savingTranscriptAccess,
    savingAnalyticsExclusion,
    loadDefaultPrompt,
    loadInboundConfig,
    handleEdit,
    handleCancel,
    handleSave,
    handleSaveInboundConfig,
    handleAddCredits,
    handleSaveDefaultPrompt,
    handleToggleTranscriptAccess,
    handleToggleAnalyticsExclusion,
    getStatusColor,
    getStatusText,
    toggleSection,
    isSectionExpanded,
    handleDeleteConfig,
    handleDeleteUser,
  } = useAdminUserDetailPage();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">User not found</p>
        <button
          onClick={() => navigate('/admin')}
          className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
        >
          Back to Admin Panel
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>

      <div className="relative px-4 py-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                <span className="text-white font-semibold text-lg">{userInfo.name.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{userInfo.name}</h1>
                <p className="text-sm text-gray-400">{userInfo.email}</p>
              </div>
            </div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor()}`}>
              {getStatusText()}
            </span>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-400/30 text-red-300 px-3 py-2 rounded-lg mb-4 flex items-center backdrop-blur-sm text-sm">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 px-3 py-2 rounded-lg mb-4 backdrop-blur-sm flex items-center justify-between text-sm">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 10-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
            <button onClick={() => setSuccessMessage(null)} className="text-emerald-200 hover:text-white">✕</button>
          </div>
        )}

        <AdminUserDetailAssistantSection
          editing={editing}
          editForm={editForm}
          setEditForm={setEditForm}
          assistantConfig={assistantConfig}
          expanded={isSectionExpanded('assistant')}
          onToggle={() => toggleSection('assistant')}
          onEdit={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          poolNumber={poolNumber}
          loadingPoolNumber={loadingPoolNumber}
        />

        <AdminUserDetailCreditsSection
          creditBalance={userInfo.credit_balance ?? 0}
          subscriptionPlanName={
            userInfo.subscription_plan_name || userInfo.subscription_plan || null
          }
          addCreditsAmount={addCreditsAmount}
          setAddCreditsAmount={setAddCreditsAmount}
          addCreditsDescription={addCreditsDescription}
          setAddCreditsDescription={setAddCreditsDescription}
          addCreditsLoading={addCreditsLoading}
          expanded={isSectionExpanded('credits')}
          onToggle={() => toggleSection('credits')}
          onAddCredits={handleAddCredits}
        />

        <div className="mb-4 rounded-lg border border-slate-700/50 bg-slate-800/40 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white">Transcript Viewer Access</h3>
              <p className="mt-1 text-xs text-slate-400">
                Controls whether this user can open full call transcripts.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={Boolean(userInfo.can_view_transcripts)}
              disabled={savingTranscriptAccess}
              onClick={() => handleToggleTranscriptAccess(!Boolean(userInfo.can_view_transcripts))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                Boolean(userInfo.can_view_transcripts) ? 'bg-cyan-500' : 'bg-slate-600'
              } ${savingTranscriptAccess ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  Boolean(userInfo.can_view_transcripts) ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-300">
            Status: {Boolean(userInfo.can_view_transcripts) ? 'Enabled' : 'Disabled'}
          </p>
        </div>

        <div className="mb-4 rounded-lg border border-slate-700/50 bg-slate-800/40 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white">Exclude from Analytics</h3>
              <p className="mt-1 text-xs text-slate-400">
                When enabled, no GA4 or Meta Pixel events will be tracked for this user.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={Boolean(userInfo.exclude_from_analytics)}
              disabled={savingAnalyticsExclusion}
              onClick={() => handleToggleAnalyticsExclusion(!Boolean(userInfo.exclude_from_analytics))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                Boolean(userInfo.exclude_from_analytics) ? 'bg-cyan-500' : 'bg-slate-600'
              } ${savingAnalyticsExclusion ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  Boolean(userInfo.exclude_from_analytics) ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-300">
            Status: {Boolean(userInfo.exclude_from_analytics) ? 'Excluded' : 'Tracked'}
          </p>
        </div>

        <AdminUserDetailDefaultPromptSection
          defaultPromptContent={defaultPromptContent}
          setDefaultPromptContent={setDefaultPromptContent}
          loadingDefaultPrompt={loadingDefaultPrompt}
          savingDefaultPrompt={savingDefaultPrompt}
          editingDefaultPrompt={editingDefaultPrompt}
          setEditingDefaultPrompt={setEditingDefaultPrompt}
          expanded={isSectionExpanded('defaultPrompt')}
          onToggle={() => toggleSection('defaultPrompt')}
          onSave={handleSaveDefaultPrompt}
          onReload={loadDefaultPrompt}
        />

        <AdminUserDetailInboundSection
          inboundConfig={inboundConfig}
          loadingInboundConfig={loadingInboundConfig}
          savingInboundConfig={savingInboundConfig}
          editingInboundConfig={editingInboundConfig}
          setEditingInboundConfig={setEditingInboundConfig}
          inboundEditForm={inboundEditForm}
          setInboundEditForm={setInboundEditForm}
          expanded={isSectionExpanded('inbound')}
          onToggle={() => toggleSection('inbound')}
          onSave={handleSaveInboundConfig}
          onReload={loadInboundConfig}
        />

        {/* Action Buttons */}
        <div className="mb-4 flex justify-between items-center gap-3">
          <button
            onClick={() => setShowDeleteUserModal(true)}
            className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 hover:text-red-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete User
          </button>
          {assistantConfig?.exists && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 hover:text-red-200 rounded-lg text-xs font-medium transition-colors"
            >
              Delete Configuration
            </button>
          )}
        </div>

        <AdminUserDetailDeleteConfigModal
          show={showDeleteModal}
          assistantConfig={assistantConfig}
          deleting={deletingConfig}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfig}
        />

        <AdminUserDetailDeleteUserModal
          show={showDeleteUserModal}
          userInfo={userInfo}
          deleteUserConfirmValue={deleteUserConfirmValue}
          setDeleteUserConfirmValue={setDeleteUserConfirmValue}
          deletingUser={deletingUser}
          deletionStats={deletionStats}
          setDeletionStats={setDeletionStats}
          onClose={() => {
            setShowDeleteUserModal(false);
            setDeleteUserConfirmValue('');
            setDeletionStats(null);
          }}
          onConfirm={handleDeleteUser}
        />
      </div>
    </div>
  );
};

export default AdminUserDetailPage;
